import { formatMoneyCents } from '../lib/format';
import {
  annualPriceCents,
  PLAN_FEATURES,
  PLAN_LIMIT,
  PLAN_LIST_PRICE_CENTS,
  PLAN_ORDER,
  PLAN_PITCH,
  planHasAnnualBilling,
  planLabel,
  RESERVAS_PER_BOOKING_CENTS,
  twelveMonthsCents,
  type BillingCycle,
  type PlanTier,
} from '../lib/plan';

type PlanPickerProps = {
  /** The radio group's name, unique per page. */
  name: string;
  value: PlanTier;
  onChange: (plan: PlanTier) => void;
  /** The plan on file, marked with `currentLabel`; signup has none. */
  current?: PlanTier;
  currentLabel?: string;
  /** What each plan would cost this business a month, discount included. */
  priceFor: (plan: PlanTier) => number;
  /** Month by month (the default) or the year up front. */
  cycle?: BillingCycle;
  /**
   * The yearly total for a plan, null when it has no monthly fee to prepay.
   * Defaults to twelve months of `priceFor` at the annual discount; Membresía
   * passes the server's own figure.
   */
  annualPriceFor?: (plan: PlanTier) => number | null;
  /** Leaves out the per-tier feature line, for the narrow signup card. */
  compact?: boolean;
  /** Only these tiers, when the server says which it sells; every tier otherwise. */
  offered?: readonly PlanTier[];
  disabled?: boolean;
};

/**
 * The four plans as one grouped radio list, cheapest first. Shared by signup,
 * the owner's Membresía page and the admin's business detail so all three read
 * the same ladder with the same words.
 */
export function PlanPicker({
  name,
  value,
  onChange,
  current,
  currentLabel,
  priceFor,
  cycle = 'monthly',
  annualPriceFor,
  compact = false,
  offered,
  disabled,
}: PlanPickerProps) {
  const yearlyFor = annualPriceFor ?? ((plan: PlanTier) => defaultAnnualPrice(plan, priceFor(plan)));
  return (
    <fieldset className="plan-picker" disabled={disabled}>
      <legend className="sr-only">Plan</legend>
      {PLAN_ORDER.filter((plan) => !offered || offered.includes(plan) || plan === current).map((plan) => (
        <label key={plan} className="plan-option" data-selected={plan === value ? 'true' : undefined}>
          <input
            type="radio"
            className="plan-radio"
            name={name}
            value={plan}
            checked={plan === value}
            onChange={() => onChange(plan)}
          />
          <span className="plan-option-body">
            <span className="plan-option-head">
              <span className="plan-name">{planLabel(plan)}</span>
              {current !== undefined && plan === current ? (
                <span className="status" data-tone="good">
                  {currentLabel}
                </span>
              ) : null}
            </span>
            <span className="plan-pitch">{PLAN_PITCH[plan]}</span>
            {compact ? null : <span className="plan-features">{featureLine(plan)}</span>}
            {compact || !PLAN_LIMIT[plan] ? null : <span className="plan-limit">{PLAN_LIMIT[plan]}</span>}
          </span>
          {cycle === 'annual' ? (
            <AnnualPlanPrice plan={plan} priceCents={priceFor(plan)} annualCents={yearlyFor(plan)} />
          ) : (
            <PlanPrice plan={plan} priceCents={priceFor(plan)} />
          )}
        </label>
      ))}
    </fieldset>
  );
}

/** Names keep their capital mid-sentence ("más Dale Agents"). */
const PROPER_NOUN = /^Dale\b/;

function featureLine(plan: PlanTier): string {
  const features = PLAN_FEATURES[plan].join(' · ');
  const previous = PLAN_ORDER[PLAN_ORDER.indexOf(plan) - 1];
  if (!previous) return features;
  const joined = PROPER_NOUN.test(features) ? features : `${features.charAt(0).toLowerCase()}${features.slice(1)}`;
  return `Todo lo de ${planLabel(previous)}, más ${joined}`;
}

function PlanPrice({ plan, priceCents }: { plan: PlanTier; priceCents: number }) {
  const list = PLAN_LIST_PRICE_CENTS[plan];
  const discounted = priceCents < list;
  return (
    <span className="plan-price">
      <span className="plan-price-amount">{formatWholeDollars(priceCents)}</span>
      <span className="plan-price-per">
        {plan === 'reservas' ? `${formatMoneyCents(RESERVAS_PER_BOOKING_CENTS)} por reserva` : 'al mes'}
      </span>
      {discounted ? <span className="plan-price-list">Lista {formatWholeDollars(list)}</span> : null}
    </span>
  );
}

function defaultAnnualPrice(plan: PlanTier, monthlyPriceCents: number): number | null {
  return planHasAnnualBilling(plan) ? annualPriceCents(monthlyPriceCents) : null;
}

/**
 * The year up front: the yearly total, the twelve monthly payments it
 * replaces, and the difference in plain words. A plan with nothing to prepay
 * keeps its monthly reading.
 */
function AnnualPlanPrice({ plan, priceCents, annualCents }: { plan: PlanTier; priceCents: number; annualCents: number | null }) {
  if (annualCents === null) return <PlanPrice plan={plan} priceCents={priceCents} />;
  const twelveMonths = twelveMonthsCents(priceCents);
  return (
    <span className="plan-price">
      <span className="plan-price-amount">{formatWholeDollars(annualCents)}</span>
      <span className="plan-price-per">al año</span>
      <span className="plan-price-was">en vez de {formatWholeDollars(twelveMonths)}</span>
      <span className="plan-price-save">Ahorras {formatWholeDollars(twelveMonths - annualCents)}</span>
    </span>
  );
}

/** "$50" for round amounts, "$42.50" otherwise, so the ladder reads at a glance. */
export function formatWholeDollars(cents: number): string {
  return cents % 100 === 0 ? `$${(cents / 100).toLocaleString('es-PA')}` : formatMoneyCents(cents);
}
